import lodash from 'lodash'
import Vue from 'vue'

import * as dirRequest from '@/api/directories/request'
import { updateVueObject } from '@/store/util'
import navigation from '@/utils/navigation'
import notification from '@/utils/notification'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import types from '../mutation-types'

function getDefaultState () {
  return {
    currentDirectory: {},
    tenancyInfo: {},
  }
}

const getters = {
  currentDirectory: state => state.currentDirectory,
  tenancyInfo: state => state.tenancyInfo,
  canCurrUserApprove (state, getters, rootState, rootGetters) {
    const isEngineer = rootGetters['user/isEngineer']
    return isEngineer && aclHelper.getAccess(tmsPermsTypes.directory.approve)
  },
  canCurrUserCreate (state, getters, rootState, rootGetters) {
    const isEngineer = rootGetters['user/isEngineer']
    const isTenant = rootGetters['user/isTenant']
    return isTenant || (isEngineer && aclHelper.getAccess(tmsPermsTypes.directory.createUpdate))
  },
  canCurrUserRemove (state, getters, rootState, rootGetters) {
    const isEngineer = rootGetters['user/isEngineer']
    return isEngineer && aclHelper.getAccess(tmsPermsTypes.directory.removeFromMaintain)
  },
}

const actions = {
  async createDirectory ({ commit }, request) {
    try {
      const directory = await dirRequest.createDirectory(request)
      commit(types.DIRECTORY_SET_CURRENT, directory)
      return directory
    } catch (err) {
      notification.handleResponseError(err, 'directory')
      throw err
    }
  },
  async loadAll ({ state }, query = { tenancyId: state.tenancyInfo.id }) {
    try {
      const list = await dirRequest.list(query)
      list.forEach(directory => checkDirectoryOrder(directory))
      return list
    } catch (err) {
      notification.handleResponseError(err, 'directory')
      return null
    }
  },
  async loadDirectory ({ commit, state }, id = state.currentDirectory.id) {
    try {
      const directory = await dirRequest.getDirectory(id)
      commit(types.DIRECTORY_SET_CURRENT, directory)
      return directory
    } catch (err) {
      notification.handleResponseError(err, 'directory')
      return null
    }
  },
  async updateDirectory ({ state, commit, dispatch }, { request = state.currentDirectory, refresh = true, headerAction }) {
    try {
      const id = state.currentDirectory.id
      await dirRequest.updateDirectory(id, request, {headerAction})
      if (refresh) {
        return await dispatch('loadDirectory', id)
      }
      return true
    } catch (err) {
      notification.handleResponseError(err, 'directory')
      throw err
    }
  },
  async submitDirectory ({ state, dispatch }, { id = state.currentDirectory.id, status, action, request = state.currentDirectory, needUpdate = true, refresh = true, headerAction }) {
    try {
      if (needUpdate) {
        await dirRequest.updateDirectory(id, request, { headerAction })
      }
      await dirRequest.changeDirectoryStatus(id, { status, action }, false, headerAction)
      if (refresh) {
        return dispatch('loadDirectory', id)
      }
    } catch (err) {
      notification.handleResponseError(err, 'directory')
      throw err
    }
  },
  async batchUpdate ({ dispatch }, {request, headerAction}) {
    try {
      await dirRequest.batchUpdate(request, headerAction)
    } catch (err) {
      notification.handleResponseError(err, 'directory')
      throw err
    }
  },
  async deleteDirectory ({ state }, { id = state.currentDirectory.id, routerName }) {
    try {
      await dirRequest.deleteDirectory(id)
      if (routerName) {
        navigation.toPage(routerName, { id: state.tenancyInfo.id })
      }
    } catch (err) {
      notification.handleResponseError(err, 'directory')
      throw err
    }
  },
  async logMaintainHistory ({ state }, request) {
    try {
      const response = await dirRequest.logMaintainHistory(request)
      return response
    } catch (err) {
      notification.handleResponseError(err, 'directory')
      throw err
    }
  },
  async listMaintainHistories ({ state }, request) {
    try {
      const response = await dirRequest.listMaintainHistories(request)
      return response.data.data
    } catch (err) {
      notification.handleResponseError(err, 'directory')
      throw err
    }
  },
}

const mutations = {
  [types.DIRECTORY_UPDATE_COMPANY] (state, { action, path, type, parentId, index }) {
    const companies = lodash.get(state.currentDirectory, path, [])
    if (action === 'ADD') {
      companies.push({
        type,
        parentId,
        order: companies.length + 1,
      })
    } else if (action === 'REMOVE') {
      companies.splice(index, 1)
    }
    const sortedCompanies = checkAndSortOrder(companies)
    updateVueObject(state.currentDirectory, path, sortedCompanies)
  },
  [types.DIRECTORY_SET_CURRENT] (state, directory) {
    checkDirectoryOrder(directory)
    updateVueObject(state, ['currentDirectory'], directory)
  },
  [types.DIRECTORY_SET_FIELD_BY_PATH] (state, { path, value, remove = false }) {
    updateVueObject(state.currentDirectory, path, value, remove)
  },
  [types.DIRECTORY_SET_ORDER] (state, { path, upward = false }) {
    const parentPath = lodash.cloneDeep(path)
    const index = parentPath.pop()
    const parent = lodash.get(state.currentDirectory, parentPath, [])
    const directory = parent[index]
    if (!Array.isArray(parent)) {
      return
    }
    if ((upward && index === 0) || (!upward && index === parent.length - 1)) {
      return
    }
    if (parent.some(item => !item.order)) {
      assignOrder(parent)
    }
    const offset = upward ? -1.1 : 1.1
    directory.order += offset
    const sortedCompanies = checkAndSortOrder(parent, true)
    updateVueObject(state.currentDirectory, parentPath, sortedCompanies)
  },
  [types.DIRECTORY_TENANCY_INFO] (state, info) {
    state.tenancyInfo = info
  },
  [types.DIRECTORY_RESET_CURRENT] (state) {
    state.currentDirectory = {}
  },
  [types.RESET_STATE] (state) {
    const stateVal = getDefaultState()
    for (const field in stateVal) {
      Vue.set(state, field, stateVal[field])
    }
  },
}

function checkAndSortOrder (array, forceFlag = false) {
  let updatedOrderFlag = true
  if (!forceFlag) {
    const emptyExistFlag = array.some(item => !!item.order)
    const notContinuousFlag = array.some((item, index) => item.order !== index + 1)
    updatedOrderFlag = emptyExistFlag || notContinuousFlag
  }
  if (updatedOrderFlag) {
    const sortedArray = lodash.orderBy(array,
      item => (item.order || item.order === 0) ? item.order : Number.MIN_VALUE)
    assignOrder(sortedArray)
    return sortedArray
  }
  return array
}

function assignOrder (array) {
  array.forEach((item, index) => {
    item.order = index + 1
  })
}

function checkDirectoryOrder (directories) {
  const needSorted = array => {
    return array.some(item => !!item.order) ||
      array.some((item, index) => item.order !== index + 1)
  }
  const mainCompanies = lodash.get(directories, 'companies', [])
  mainCompanies.forEach(mainCompany => {
    let subCompanies = lodash.get(mainCompany, 'subCompanies', [])
    if (needSorted(subCompanies)) {
      const sortedCompanies = lodash.orderBy(subCompanies,
        item => (item.order || item.order === 0) ? item.order : Number.MIN_VALUE)
      assignOrder(sortedCompanies)
      lodash.set(mainCompany, 'subCompanies', sortedCompanies)
      subCompanies = sortedCompanies
    }
    subCompanies.forEach(subCompany => {
      let details = lodash.get(subCompany, 'subDetails', [])
      if (needSorted(details)) {
        const sortedDetails = lodash.orderBy(details,
          item => (item.order || item.order === 0) ? item.order : Number.MIN_VALUE)
        lodash.set(subCompany, 'subDetails', sortedDetails)
        details = sortedDetails
      }
    })
  })
}

export default {
  namespaced: true,
  state: getDefaultState(),
  getters,
  actions,
  mutations,
}
